/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, TextField, Popover, Tooltip, Button,
} from '@mui/material';
import { ChromePicker } from 'react-color';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import InfoIcon from '@mui/icons-material/Info';
import PaletteIcon from '@mui/icons-material/Palette';
import AppsIcon from '@mui/icons-material/Apps';
import PhotoIcon from '@mui/icons-material/Photo';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import SettingsMenu from './components/SettingsMenu';
import UploadAvatar from '../common/components/upload/UploadAvatar';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useCatch } from '../reactHelper';
import { useAdministrator } from '../common/util/permissions';
import { useTheme } from '@mui/material/styles';
import { getThemePaths } from '../common/auth/themePaths';

const RevendaPage = () => {
  const t = useTranslation();
  const admin = useAdministrator();
  const theme = useTheme();

  const [item, setItem] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentColorField, setCurrentColorField] = useState(null);
  const [backgroundAvailable, setBackgroundAvailable] = useState({ light: false, dark: false });
  const [iconFile, setIconFile] = useState(null);

  // 🌈 padrões de gradiente
  const lightPattern = `linear-gradient(135deg, #e6f0ff 0%, #d0e1ff 25%, #ffffff 50%, #e6f0ff 100%)`;
  const darkPattern = `linear-gradient(135deg, #0a192f 0%, #0d1f3a 25%, #122b4a 50%, #0a192f 100%)`;
  const currentMode = theme?.palette?.mode || 'light';

  // theme seguro para evitar crashes em divider/background
  const safeTheme = theme?.palette
    ? theme
    : { palette: { divider: '#ccc', background: { paper: '#fff' }, text: { primary: '#000' } } };

  // estilos padrão, alinhados ao UserPage
  const summarySx = {
    bgcolor: safeTheme.palette.background.paper,
    color: safeTheme.palette.text.primary,
    borderBottom: `1px solid ${safeTheme.palette.divider}`,
    '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5 },
  };
  const detailsSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    p: { xs: 2, sm: 2.5 },
    bgcolor: safeTheme.palette.background.paper,
  };
  const rowSx = { display: 'flex', width: '100%', gap: 2, flexDirection: { xs: 'column', sm: 'row' } };
  const fieldSx = { flex: 1, minWidth: 0 };

  // Centraliza todos os paths de imagens da revenda com base no item atual
  const paths = useMemo(() => getThemePaths(item), [item?.code, item?.background]);

  // 🖼️ checagem de imagem de fundo
  const checkImage = useCallback((src) => {
    return new Promise((resolve) => {
      if (!src) return resolve(false);
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });
  }, []);

  const verifyBackgrounds = useCallback(async () => {
    if (!item?.code) return;
    const [lightOk, darkOk] = await Promise.all([
      checkImage(paths?.backgroundLight),
      checkImage(paths?.backgroundDark),
    ]);
    setBackgroundAvailable({ light: lightOk, dark: darkOk });
  }, [item?.code, paths?.backgroundLight, paths?.backgroundDark, checkImage]);

  useEffect(() => {
    verifyBackgrounds();
  }, [verifyBackgrounds]);

  const getLogo = (rev) => {
    if (rev.logoPreview) {
      return { preview: rev.logoPreview };
    }
    const mode = currentMode === 'dark' ? 'dark' : 'light';
    return `/api/revendas/logo/${rev.code}?theme=${mode}`;
  };

  const handleColorClick = (field, event) => {
    setCurrentColorField(field);
    setAnchorEl(event.currentTarget);
  };
  const handleColorClose = () => {
    setAnchorEl(null);
    setCurrentColorField(null);
  };
  const handleColorChange = (color) => {
    setItem({ ...item, [currentColorField]: color.hex });
    if (currentColorField === 'background') verifyBackgrounds();
  };

  const handleLogoDrop = useCatch(async (acceptedFiles) => {
    if (!item?.id || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const response = await fetch(`/api/revendas/${item.id}/logo`, {
      method: 'POST',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!response.ok) throw new Error(await response.text());
    const filename = await response.text();
    const previewUrl = URL.createObjectURL(file);
    setItem({ ...item, logo: filename, logoPreview: previewUrl });
  });

  const handleIconInput = useCatch(async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setIconFile(file);
        const attributes = {
          ...item.attributes,
          iconBase64: base64,
          iconFileName: file.name,
        };
        setItem({ ...item, attributes });
      };
      reader.readAsDataURL(file);
    }
  });

  const getIconPreview = () => {
    if (iconFile) return URL.createObjectURL(iconFile);
    if (item?.attributes?.iconBase64) return item.attributes.iconBase64;
    return null;
  };

  const validate = () => item && item.code && item.title && item.url;

  return (
    <EditItemView
      endpoint="revendas"
      item={item}
      setItem={setItem}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['userAdmin', 'sharedRevendas', 'sharedRevenda']}
    >
      {item && (
        <>
          {/* ================= SEÇÃO OBRIGATÓRIA ================= */}
          <Accordion defaultExpanded disableGutters sx={{ border: `1px solid ${safeTheme.palette.divider}`, borderRadius: 2, mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <BusinessIcon fontSize="small" />
              <Typography variant="subtitle1">{t('sharedRequired')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={detailsSx}>
              <Box sx={rowSx}>
                {/* Logo */}
                <Box sx={fieldSx}>
                  <UploadAvatar
                    accept="image/*"
                    file={getLogo(item)}
                    maxSize={Number.MAX_SAFE_INTEGER}
                    onDrop={handleLogoDrop}
                    caption={t('attributeResaleLogo')}
                  />
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}
                  >
                    {t('sharedAllowed')}<br />{t('sharedMaxSize')}
                  </Typography>
                </Box>

                {/* Campos principais */}
                <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {admin && (
                    <TextField
                      fullWidth
                      size="small"
                      value={item.code || ''}
                      onChange={(e) => setItem({ ...item, code: e.target.value })}
                      label={t('sharedCodeInternal')}
                    />
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    value={item.title || ''}
                    onChange={(e) => setItem({ ...item, title: e.target.value })}
                    label={t('sharedName')}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    value={item.url || ''}
                    onChange={(e) => setItem({ ...item, url: e.target.value })}
                    label={t('sharedUrl')}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* ================= SEÇÃO INFORMAÇÕES ================= */}
          <Accordion disableGutters sx={{ border: `1px solid ${safeTheme.palette.divider}`, borderRadius: 2, mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <InfoIcon fontSize="small" />
              <Typography variant="subtitle1">{t('sharedInfo')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={detailsSx}>
              {[
                ['template', 'phone', 'email'],
                ['document', 'address', 'neighborhood'],
                ['city', 'state', 'postalcode'],
              ].map((row, idx) => (
                <Box key={idx} sx={rowSx}>
                  {row.map((field) => (
                    <TextField
                      key={field}
                      fullWidth
                      size="small"
                      value={item[field] || ''}
                      onChange={(e) => setItem({ ...item, [field]: e.target.value })}
                      label={t(`shared${field.charAt(0).toUpperCase() + field.slice(1)}`)}
                    />
                  ))}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>

          {/* ================= SEÇÃO CUSTOMIZAÇÃO ================= */}
          <Accordion disableGutters sx={{ border: `1px solid ${safeTheme.palette.divider}`, borderRadius: 2, mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <PaletteIcon fontSize="small" />
              <Typography variant="subtitle1">{('CUSTOMIZAÇÃO')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={detailsSx}>
              <Box sx={rowSx}>
                {[1, 2, 3, 4].map((num) => {
                  const field = `color${num}`;
                  const defaultValue = num === 3 ? '#cccccc' : '#1976d2';
                  return (
                    <TextField
                      key={num}
                      fullWidth
                      size="small"
                      value={item[field] || defaultValue}
                      onChange={(e) => setItem({ ...item, [field]: e.target.value })}
                      label={`${t('attributeColor')} ${num}`}
                      InputProps={{
                        startAdornment: (
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: item[field] || defaultValue,
                              marginRight: 1,
                              border: '1px solid #ccc',
                              cursor: 'pointer',
                            }}
                            onClick={(e) => handleColorClick(field, e)}
                          />
                        ),
                      }}
                    />
                  );
                })}

                {/* campo background */}
                <TextField
                  fullWidth
                  size="small"
                  value={item.background || '#ffffff'}
                  onChange={(e) => {
                    setItem({ ...item, background: e.target.value });
                    verifyBackgrounds();
                  }}
                  label={t('attributeBackground')}
                  InputProps={{
                    startAdornment: (
                      <Tooltip
                        title={
                          backgroundAvailable.light || backgroundAvailable.dark
                            ? 'Imagem de fundo disponível'
                            : 'Gradiente padrão'
                        }
                        arrow
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '4px',
                            background:
                              (backgroundAvailable.light && paths?.backgroundLight && currentMode === 'light')
                                ? `url(${paths.backgroundLight})`
                                : (backgroundAvailable.dark && paths?.backgroundDark && currentMode === 'dark')
                                  ? `url(${paths.backgroundDark})`
                                  : (currentMode === 'dark' ? darkPattern : lightPattern),
                            backgroundSize: 'cover',
                            marginRight: 1,
                            border: '1px solid #ccc',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                          onClick={(e) => handleColorClick('background', e)}
                        />
                      </Tooltip>
                    ),
                  }}
                />
              </Box>

              {/* color picker */}
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleColorClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <ChromePicker
                  color={item[currentColorField] || '#1976d2'}
                  onChange={handleColorChange}
                />
              </Popover>
            </AccordionDetails>
          </Accordion>

          {/* ================= SEÇÃO APLICATIVOS ================= */}
          <Accordion disableGutters sx={{ border: `1px solid ${safeTheme.palette.divider}`, borderRadius: 2, mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <AppsIcon fontSize="small" />
              <Typography variant="subtitle1">{t('sharedApps')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={detailsSx}>
              <Box sx={rowSx}>
                <TextField
                  fullWidth
                  size="small"
                  value={item.googleplay || ''}
                  onChange={(e) => setItem({ ...item, googleplay: e.target.value })}
                  label={t('sharedGooglePlay')}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={item.applestore || ''}
                  onChange={(e) => setItem({ ...item, applestore: e.target.value })}
                  label={t('sharedAppleStore')}
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* ================= SEÇÃO ÍCONES ================= */}
          <Accordion disableGutters sx={{ border: `1px solid ${safeTheme.palette.divider}`, borderRadius: 2, mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <PhotoIcon fontSize="small" />
              <Typography variant="subtitle1">{('Favicons')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={detailsSx}>
              <Typography variant="body2" color="text.secondary">
                {t('sharedIconsDescription')}
              </Typography>

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="icon-upload"
                type="file"
                onChange={handleIconInput}
              />
              <label htmlFor="icon-upload">
                <Button variant="contained" component="span">
                  {t('sharedSelectIcon')}
                </Button>
              </label>

              {getIconPreview() && (
                <Box mt={2}>
                  <Typography variant="body2">{t('sharedPreview')}</Typography>
                  <Box display="flex" flexWrap="wrap" mt={1}>
                    <Box
                      component="img"
                      src={getIconPreview()}
                      alt="Icon preview"
                      sx={{
                        width: 64,
                        height: 64,
                        m: 0.5,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: `1px solid ${safeTheme.palette.divider}`,
                      }}
                    />
                  </Box>
                </Box>
              )}

              {item?.attributes?.iconFileName && (
                <Typography variant="caption">
                  {t('sharedCurrentFile')}: {item.attributes.iconFileName}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ================= ATRIBUTOS EXTRAS ================= */}
          <EditAttributesAccordion
            attributes={item.attributes}
            setAttributes={(attributes) => setItem({ ...item, attributes })}
            definitions={{}}
          />
        </>
      )}
    </EditItemView>
  );
};

export default RevendaPage;
